import moment from 'moment';
import isString from 'lodash/isString';
import { CREDIT_CARD_MAP } from 'config/ui';
import getCreditCardType from 'utils/getCreditCardType';
import omit from 'lodash/omit';

import dialogActions from 'core/dialogs/actions';
import dataActions from './actions';
import pageActions from '../page/actions';
import fieldActions from '../fields/actions';
import groupActions from '../groups/actions';
import plaidActions from '../plaid/actions';

const getTransformedData = (data, fields, currentData = {}, forceUpdate = false) => {
  if (!data || !fields) {
    return data || {};
  }

  return Object.keys(data).reduce((newData, key) => {
    const { fieldType: type, hidden } = fields[key] || {};
    const newDataForKey = forceUpdate || hidden ? data[key] : currentData[key] || data[key];
    if (
      fields.appRequest_externalPaymentAccountNumber &&
      type !== 'AMOUNT_SELECTOR' &&
      fields[key]?.selectorValues?.length === 1
    ) {
      return {
        ...newData,
        [key]: fields[key].selectorValues[0].value,
      };
    }

    if (newDataForKey) {
      if (type === 'LOCAL_DATE') {
        return {
          ...newData,
          [key]: moment(newDataForKey).format('MM/DD/YYYY'),
        };
      }
      if (
        type === 'NUMBER' ||
        type === 'DECIMAL' ||
        type === 'AMOUNT' ||
        type === 'AMOUNT_SLIDER' ||
        type === 'AMOUNT_SELECTOR'
      ) {
        const numberFloat = isString(newDataForKey)
          ? parseFloat(newDataForKey.replace(/,/g, ''))
          : newDataForKey;
        // check for NaN
        return {
          ...newData,
          [key]: Number.isNaN(numberFloat) ? null : numberFloat,
        };
      }
    }

    return {
      ...newData,
      [key]: newDataForKey,
    };
  }, {});
};

export default (state = {}, action) => {
  switch (action.type) {
    case plaidActions.FETCH_ACCOUNTS_SUCCESS:
    case plaidActions.HANDLE_VERIFICATION_QUESTIONS:
    case dialogActions.SEND_CODE_SUCCESS:
    case pageActions.GO_SUCCESS: {
      const {
        data,
        meta: { fields },
      } = action.payload.data;
      /** HACK: This should be removed when the prod start page
       * is replaced with the product showcase
       */
      if (action.payload.data.meta?.page?.name === 'prodstart') {
        return {
          productLookup: '',
        };
      }
      const dataLists = data && Object.keys(data).filter((dataKey) => dataKey.includes('___list'));
      if (data && data.questions) {
        return {
          ...data,
          ...data.questions.reduce((newState, question) => {
            const clientId = `KBA_questions${question.id}`;
            return {
              ...newState,
              [clientId]: null,
            };
          }, {}),
        };
      }
      if (dataLists && dataLists.length > 0) {
        const dataWithoutLists = omit(data, dataLists);
        return {
          ...getTransformedData(dataWithoutLists, fields),
          ...dataLists.reduce(
            (obj, dataListKey) => ({
              ...obj,
              [dataListKey]:
                (data[dataListKey].length > 0 &&
                  data[dataListKey].map((listData) => {
                    const transformedData = getTransformedData(listData, fields);
                    const firstNameKey = Object.keys(transformedData).find((key) =>
                      key.includes('firstName'),
                    );
                    const lastNameKey = Object.keys(transformedData).find((key) =>
                      key.includes('lastName'),
                    );
                    const emailAddressKey = Object.keys(transformedData).find((key) =>
                      key.includes('emailAddress'),
                    );
                    return {
                      ...getTransformedData(listData, fields),
                      isEditing:
                        !transformedData[firstNameKey] &&
                        !transformedData[lastNameKey] &&
                        !transformedData[emailAddressKey],
                      isNew:
                        !transformedData[firstNameKey] &&
                        !transformedData[lastNameKey] &&
                        !transformedData[emailAddressKey],
                    };
                  })) ||
                [],
            }),
            {},
          ),
        };
      }
      return getTransformedData(data, fields);
    }
    case groupActions.UPLOAD_SUCCESS: {
      const {
        data,
        meta: { fields },
      } = action.payload.data;
      return getTransformedData(data, fields);
    }
    case pageActions.UPDATE_PAGE_DATA: {
      const { data } = action.payload;
      return {
        ...state,
        ...getTransformedData(data.data, data.meta?.fields),
      };
    }
    case fieldActions.RERENDER_SUCCESS:
    case fieldActions.RERENDER_FAILED: {
      const { data, meta } = action.payload.data;
      const fields = meta?.fields;
      const { forceUpdate } = action.payload;

      const dataLists = data && Object.keys(data).filter((key) => key.includes('___list'));

      if (dataLists && dataLists.length > 0) {
        const dataWithoutLists = omit(data, dataLists);

        return {
          ...getTransformedData(dataWithoutLists, fields, state, forceUpdate),
          ...dataLists.reduce(
            (obj, dataListKey) => ({
              ...obj,
              [dataListKey]:
                (data[dataListKey].length > 0 &&
                  data[dataListKey].map((listData) => {
                    const transformedData = getTransformedData(
                      listData,
                      fields,
                      state,
                      forceUpdate,
                    );

                    const firstNameKey = Object.keys(transformedData).find((key) =>
                      key.includes('firstName'),
                    );
                    const lastNameKey = Object.keys(transformedData).find((key) =>
                      key.includes('lastName'),
                    );
                    const emailAddressKey = Object.keys(transformedData).find((key) =>
                      key.includes('emailAddress'),
                    );

                    const isEmpty =
                      !transformedData[firstNameKey] &&
                      !transformedData[lastNameKey] &&
                      !transformedData[emailAddressKey];

                    return {
                      ...transformedData,
                      isEditing: isEmpty,
                      isNew: isEmpty,
                    };
                  })) ||
                [],
            }),
            {},
          ),
        };
      }

      return getTransformedData(data, fields, state, forceUpdate);
    }
    case dataActions.CHANGE: {
      const { key, value } = action.payload;
      if (key === 'appRequest_linkedPaymentCard_accountNumber') {
        return {
          ...state,
          appRequest_linkedPaymentCard_cardNetwork:
            CREDIT_CARD_MAP[getCreditCardType(value)] || 'INVALID_ACCOUNT_NUMBER',
          [key]: value,
        };
      }
      return {
        ...state,
        [key]: value,
      };
    }
    case dataActions.CHANGE_AUTO_COMPLETE:
      return {
        ...state,
        [action.payload.key]: action.payload.option && action.payload.option.id,
      };
    case dataActions.CHANGE_LISTGROUP: {
      const { key, listGroupName, listIndex, value } = action.payload;
      return {
        ...state,
        [listGroupName]: state[listGroupName].map((g, i) => {
          if (i === listIndex) {
            return {
              ...g,
              [key]: value,
            };
          }
          return g;
        }),
      };
    }
    case groupActions.NEXT_FAILED: {
      const { data } = action.payload;
      return {
        ...state,
        ...getTransformedData(data.data, data.meta?.fields),
      };
    }
    case pageActions.NEXT_FAILED:
    case groupActions.UPLOAD_FAILED: {
      const { data, fields } = action.payload;
      return {
        ...state,
        ...getTransformedData(data.data, fields, state),
      };
    }
    case pageActions.SAVE_SIGNATURE_SUCCESS:
      return {
        ...state,
        agreements: state.agreements.map((a) => {
          if (a.id === action.payload.id) {
            return {
              ...a,
              requireESign: false,
              requireManualSign: false,
            };
          }
          return a;
        }),
      };
    default:
      return state;
  }
};
